import React from 'react'
import styled from 'styled-components'
import BuyButton from '../../MwPage/components/BuyButton'
import { useRevealProps } from '../../MwPage/components/Reveal'
import { IDS } from '../../MwPage/content'
import { fillOffer } from '../../MwPage/offer'
import {
  blue,
  blue2,
  cyan,
  darkLine,
  darkRaise,
  DotGrid,
  Eyebrow,
  Hi,
  iceLine,
  ink,
  Lede,
  muted,
  onDark2,
  paper,
  sans,
  Section,
  teal,
  ui,
} from '../../MwPage/styled'
import { useFocus } from '../focus'
import { useScores } from '../scores'
import { Bullets, Callout, DarkPanel, Fig, FigNote, FigTitle, H2, Kicker, Note, PanelInner, Wrap } from '../styled'

const FigHead = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 10px;
  margin-bottom: 8px;

  ${FigTitle} {
    margin: 0;
  }
`

const Reset = styled.button`
  flex: none;
  padding: 6px 14px;
  border-radius: 999px;
  border: 1.5px solid ${darkLine};
  background: ${darkRaise};
  color: #c8cbd0;
  font: 500 13px/1.3 ${sans};
  cursor: pointer;

  &:hover {
    border-color: ${cyan};
  }
`

const Row = styled.div`
  padding: 10px 0 4px;
  border-top: 1.5px solid ${darkLine};

  &:first-child {
    border-top: 0;
  }
`

const RowHead = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  gap: 10px;

  b {
    display: block;
    font: 700 15px/1.3 ${sans};
    color: #ffffff;
  }

  span {
    display: block;
    font-size: 12.5px;
    line-height: 1.4;
    color: ${onDark2};
  }
`

const Score = styled.div`
  flex: none;
  display: flex;
  align-items: center;
  gap: 8px;

  output {
    min-width: 30px;
    text-align: right;
    font: 800 15px/1 ${ui};
    color: #ffffff;
  }
`

const Focus = styled.i`
  display: ${(props) => (props.$on ? 'inline-block' : 'none')};
  padding: 3px 9px;
  border-radius: 999px;
  background: linear-gradient(90deg, ${blue}, ${blue2});
  color: #ffffff;
  font: 800 10px/1.3 ${ui};
  letter-spacing: 0.08em;
`

const Track = styled.div`
  position: relative;
  height: 28px;
  display: flex;
  align-items: center;
`

// the prototype's .sl: a 10px track filled to the thumb in the function's colour, cyan when it is a focus.
// --p is the fill and --c the colour, set inline from the score
const Slider = styled.input`
  -webkit-appearance: none;
  appearance: none;
  position: relative;
  z-index: 2;
  width: 100%;
  height: 28px;
  margin: 0;
  background: transparent;
  cursor: pointer;

  &::-webkit-slider-runnable-track {
    height: 10px;
    border-radius: 999px;
    background: linear-gradient(to right, var(--c) var(--p), #2c2f34 var(--p));
  }

  &::-webkit-slider-thumb {
    -webkit-appearance: none;
    box-sizing: border-box;
    width: 22px;
    height: 22px;
    margin-top: -6px;
    border-radius: 50%;
    background: #ffffff;
    border: 4px solid var(--c);
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.4);
  }

  &::-moz-range-track {
    height: 10px;
    border-radius: 999px;
    background: #2c2f34;
  }

  &::-moz-range-progress {
    height: 10px;
    border-radius: 999px;
    background: var(--c);
  }

  &::-moz-range-thumb {
    box-sizing: border-box;
    width: 22px;
    height: 22px;
    border-radius: 50%;
    background: #ffffff;
    border: 4px solid var(--c);
  }

  &:focus-visible {
    outline: 2px solid ${cyan};
    outline-offset: 2px;
    border-radius: 999px;
  }
`

// the target mark at 4 out of 5
const Tick = styled.i`
  position: absolute;
  z-index: 1;
  top: 3px;
  bottom: 3px;
  left: calc(11px + (100% - 22px) * 0.75);
  width: 2px;
  margin-left: -1px;
  border-radius: 1px;
  background: ${cyan};
  pointer-events: none;
`

// the result card: white, with the gradient as its border
const Result = styled.div`
  margin-top: 4px;
  padding: 22px 20px;
  border: 2px solid transparent;
  border-radius: 28px;
  background: linear-gradient(${paper}, ${paper}) padding-box, linear-gradient(90deg, ${blue}, ${blue2}) border-box;
  color: ${muted};
  display: flex;
  flex-direction: column;
  gap: 14px;

  ${Eyebrow} {
    align-self: flex-start;
  }

  h3 {
    margin: 0;
    font: 800 clamp(1.3rem, 2.4vw, 1.65rem) / 1.2 ${sans};
    letter-spacing: -0.03em;
    color: ${ink};
  }
`

const MeterLabel = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: baseline;
  gap: 10px;
  font: 700 13px/1.3 ${sans};
  color: ${muted};

  b {
    font: 800 24px/1 ${ui};
    letter-spacing: -0.03em;
    color: ${teal};
  }
`

const Meter = styled.div`
  position: relative;
  height: 12px;
  border-radius: 999px;
  background: ${iceLine};

  i {
    position: absolute;
    left: 0;
    top: 0;
    bottom: 0;
    border-radius: 999px;
    background: linear-gradient(90deg, ${blue}, ${blue2});
    transition: width 0.25s ease;
  }

  u {
    position: absolute;
    left: 75%;
    top: -4px;
    bottom: -4px;
    width: 2px;
    background: ${cyan};
  }
`

const MeterNote = styled.p`
  margin: -6px 0 0;
  font-size: 12px;
  color: ${muted};
`

const SliderRow = ({ area, score, focus, onChange }) => {
  const { scorer } = useFocus()
  const fill = `calc(11px + (100% - 22px) * ${(score - 1) / 4})`
  return (
    <Row>
      <RowHead>
        <div>
          <b>{area.name}</b>
          <span>{area.sub}</span>
        </div>
        <Score>
          <Focus $on={focus}>{scorer.focus}</Focus>
          <output>{score}/5</output>
        </Score>
      </RowHead>
      <Track>
        <Slider
          type="range"
          min="1"
          max="5"
          step="1"
          value={score}
          aria-label={scorer.sliderLabel(area.name)}
          onChange={(event) => onChange(area.key, Number(event.target.value))}
          style={{ '--p': fill, '--c': focus ? blue2 : '#4b8ca3' }}
        />
        <Tick aria-hidden="true" />
      </Track>
    </Row>
  )
}

/** "Find where you are": seven sliders and the targeted plan they add up to (see ../scorer.js). */
const ScorerSection = ({ offer }) => {
  const { scorer } = useFocus()
  const { scores, set, reset, result } = useScores()
  const note = fillOffer(scorer.result.note[0], offer)

  return (
    <Section id={IDS.where} style={{ paddingTop: 0 }}>
      <Wrap>
        <DarkPanel $win {...useRevealProps()}>
          <DotGrid aria-hidden="true" />
          <PanelInner>
            <Eyebrow>{scorer.eyebrow}</Eyebrow>
            <H2>
              {scorer.headline} <Hi>{scorer.headlineAccent}</Hi>
            </H2>
            <Callout>{scorer.callout}</Callout>
            <Lede>{scorer.lede}</Lede>
            <Fig $raised>
              <FigHead>
                <FigTitle>{scorer.figTitle}</FigTitle>
                <Reset type="button" onClick={reset}>
                  {scorer.reset}
                </Reset>
              </FigHead>
              <FigNote style={{ margin: '0 0 6px' }}>{scorer.figNote}</FigNote>
              <div>
                {scorer.areas.map((area) => (
                  <SliderRow
                    key={area.key}
                    area={area}
                    score={scores[area.key]}
                    focus={result.focus.includes(area.key)}
                    onChange={set}
                  />
                ))}
              </div>
              <FigNote>{scorer.example}</FigNote>
            </Fig>
            <Result aria-live="polite">
              <Eyebrow>
                {scorer.result.pill} · {result.stage}
              </Eyebrow>
              <h3>{result.headline}</h3>
              <MeterLabel>
                <span>{scorer.readiness}</span>
                <b>{result.pct}%</b>
              </MeterLabel>
              <Meter>
                <i style={{ width: `${result.pct}%` }} />
                <u aria-hidden="true" />
              </Meter>
              <MeterNote>{scorer.result.meterNote}</MeterNote>
              <Bullets>
                {result.bullets.map(([label, text]) => (
                  <li key={label}>
                    <b>{label}:</b> {text}
                  </li>
                ))}
              </Bullets>
              <Kicker style={{ marginTop: 6 }}>{scorer.result.next30}</Kicker>
              <Bullets $plan>
                {scorer.planLabels.map((week, index) => (
                  <li key={week}>
                    <b>{week}</b>
                    <span>{result.plan[index]}</span>
                  </li>
                ))}
              </Bullets>
              <BuyButton $block style={{ marginTop: 6 }}>
                {scorer.result.cta}
              </BuyButton>
              <Note $center>
                {note}
                {scorer.result.note[1]}
              </Note>
            </Result>
          </PanelInner>
        </DarkPanel>
      </Wrap>
    </Section>
  )
}

export default ScorerSection
